/**
 * useTravelTripInfo — read + update the per-trip context (party + relationship,
 * interests, budget, pace, dietary, notes) that steers the AI.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { travelApi } from '../api';
import type { TravelTripInfoResponse, UpdateTravelTripInfoInput } from '../api';
import { queryKeys } from '@/lib/query-keys';

export function useTravelTripInfo(tripId: string | undefined) {
  const queryClient = useQueryClient();

  const infoQuery = useQuery({
    enabled: !!tripId,
    queryKey: tripId ? queryKeys.travel.tripInfo(tripId) : queryKeys.travel.disabled('tripInfo'),
    queryFn: () => travelApi.getTravelTripInfo(tripId!),
  });

  const updateMut = useMutation({
    mutationFn: (input: UpdateTravelTripInfoInput) =>
      travelApi.updateTravelTripInfo(tripId!, input),
    onSuccess: (res) => {
      if (!tripId) return;
      queryClient.setQueryData<{ info: TravelTripInfoResponse | null }>(
        queryKeys.travel.tripInfo(tripId),
        { info: res.info },
      );
    },
  });

  return {
    info: (infoQuery.data?.info ?? null) as TravelTripInfoResponse | null,
    loading: infoQuery.isLoading,
    update: (input: UpdateTravelTripInfoInput) => updateMut.mutateAsync(input),
    isSaving: updateMut.isPending,
  };
}
